import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { useGetReprocessStatusQuery } from "@/services/store-conciliation";
import {
  setConciliationStatus,
} from "@/pages/conciliation/stores/conciliation/components/divergence-list/ducks/filter/hooks/statusConciliation";

interface ReprocessContextType {
  isReprocessing: boolean;
  reprocessCompleted: boolean;
  reprocessFailed: boolean;
  acknowledgeCompletion: () => void;
  startTracking: () => void;
}

const ReprocessContext = createContext<ReprocessContextType | null>(null);

interface ReprocessProviderProps {
  conciliationId: number;
  children: React.ReactNode;
}

export const ReprocessProvider: React.FC<ReprocessProviderProps> = ({
  conciliationId,
  children,
}) => {
  const dispatch = useDispatch();
  const [reprocessCompleted, setReprocessCompleted] = React.useState(false);
  const [reprocessFailed, setReprocessFailed] = React.useState(false);
  const [isPolling, setIsPolling] = React.useState(false);
  const prevIsProcessingRef = useRef<boolean | null>(null);

  const shouldSkip = isNaN(conciliationId);

  const pollingInterval = isPolling ? 10_000 : 0;

  const { data, isLoading, refetch } = useGetReprocessStatusQuery(
    { conciliationId },
    {
      skip: shouldSkip,
      pollingInterval,
      skipPollingIfUnfocused: true,
    }
  );

  const isReprocessing = data?.isProcessing ?? false;

  // When processing detected, lock UI and start polling
  useEffect(() => {
    if (isLoading || shouldSkip) return;

    if (isReprocessing && !isPolling) {
      setIsPolling(true);
      dispatch(
        setConciliationStatus({
          id: String(conciliationId),
          isClosed: true,
        })
      );
    }

    // Detect transition: was processing -> stopped
    if (prevIsProcessingRef.current === true && !isReprocessing) {
      setIsPolling(false);
      const jobState = data?.job?.state;
      if (jobState === "failed") {
        setReprocessFailed(true);
      }
      setReprocessCompleted(true);
    }

    prevIsProcessingRef.current = isReprocessing;
  }, [isReprocessing, isLoading, shouldSkip, data, isPolling, conciliationId, dispatch]);

  // Called by async flow after job is created — lock immediately + start polling
  const startTracking = useCallback(() => {
    // Lock UI immediately
    dispatch(
      setConciliationStatus({
        id: String(conciliationId),
        isClosed: true,
      })
    );
    setIsPolling(true);
    setTimeout(() => refetch(), 1_000);
  }, [refetch, dispatch, conciliationId]);

  // Reload page to get fresh data (same pattern as sync)
  const acknowledgeCompletion = useCallback(() => {
    setReprocessCompleted(false);
    setReprocessFailed(false);
    dispatch(
      setConciliationStatus({
        id: String(conciliationId),
        isClosed: false,
      })
    );
    window.location.reload();
  }, [dispatch, conciliationId]);

  const contextValue = useMemo(
    () => ({
      isReprocessing,
      reprocessCompleted,
      reprocessFailed,
      acknowledgeCompletion,
      startTracking,
    }),
    [isReprocessing, reprocessCompleted, reprocessFailed, acknowledgeCompletion, startTracking]
  );

  return (
    <ReprocessContext.Provider value={contextValue}>
      {children}
    </ReprocessContext.Provider>
  );
};

export const useReprocess = (): ReprocessContextType => {
  const context = useContext(ReprocessContext);
  if (!context) {
    throw new Error("useReprocess must be used within ReprocessProvider");
  }
  return context;
};
